import React from 'react';

import { useTranslation } from 'react-i18next';
import { TooltipProps } from 'recharts';

import { FORECAST_COLORS } from './constants';
import { ForecastPoint } from './helpers';
import { TooltipBox } from './styles';

/**
 * Carries two facts the chart cannot show without lying: the current week's partial sales, and
 * the fact that a forecast was adjusted.
 */
const ForecastTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  const { t } = useTranslation('translation');

  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0].payload as ForecastPoint;
  const sales = point.realizedSales ?? point.forecastSales;
  const isForecast = point.realizedSales === null && point.forecastSales !== null;

  const rows: Array<{ color?: string; key: string; label: string; value: number | string }> = [];

  if (sales !== null && sales !== undefined) {
    rows.push({
      key: 'sales',
      color: FORECAST_COLORS.sales,
      label: isForecast
        ? t('productOptimalStock.sales_forecast.forecast')
        : t('productOptimalStock.sales_forecast.sales'),
      value: sales,
    });
  }

  if (point.realizedSoFar !== null) {
    rows.push({
      key: 'soFar',
      label: t('productOptimalStock.sales_forecast.sold_so_far'),
      value: point.realizedSoFar,
    });
  }

  if (point.storeStock !== null) {
    rows.push({
      key: 'store',
      color: FORECAST_COLORS.storeStock,
      label: t('productOptimalStock.sales_forecast.store_stock'),
      value: point.storeStock,
    });
  }

  if (point.warehouseStock !== null) {
    rows.push({
      key: 'warehouse',
      color: FORECAST_COLORS.warehouseStock,
      label: t('productOptimalStock.sales_forecast.warehouse_stock'),
      value: point.warehouseStock,
    });
  }

  if (point.outstandingSupply !== null) {
    rows.push({
      key: 'supply',
      color: FORECAST_COLORS.outstandingSupply,
      label: t('productOptimalStock.sales_forecast.outstanding_supply'),
      value: point.outstandingSupply,
    });
  }

  if (point.projectedStock !== null) {
    rows.push({
      key: 'projected',
      color: FORECAST_COLORS.projectedStock,
      label: t('productOptimalStock.sales_forecast.projected_stock'),
      value: point.projectedStock,
    });
  }

  return (
    <TooltipBox>
      <strong>{point.label}</strong>
      <dl>
        {rows.map((row) => (
          <React.Fragment key={row.key}>
            <dt>
              {row.color && <i style={{ backgroundColor: row.color }} aria-hidden="true" />}
              {row.label}
            </dt>
            <dd>{row.value}</dd>
          </React.Fragment>
        ))}
      </dl>
      {point.isFloored && <p>{t('productOptimalStock.sales_forecast.floored')}</p>}
      {point.isCapped && <p>{t('productOptimalStock.sales_forecast.capped')}</p>}
    </TooltipBox>
  );
};

export default ForecastTooltip;
